// Contracts — work.weern.com DESIGNER cabinet + Web Push (2026-06-24).
// The designer cabinet is shown when the contractor has the `designer` capability
// (designer ⊇ generator). It is read-only over the available-source pool: the
// designer cannot claim work here — claiming happens in the Figma plugin. Money is
// HIDDEN everywhere for now (the price base lives in D1: registrations.payout_* +
// designer_pack_pay), so NO pay field crosses these schemas. Shapes from
// migrations/0008 (registrations/photos), 0010 (ticket_pdfs/manifests), 0012 (push).
import { z } from 'zod';

// ─── feed (available sources, read-only, grouped shot → pdf) ──────────────────
export const WorkDesignerFeedItemSchema = z.object({
  id: z.string(),
  kind: z.enum(['shot', 'pdf']),          // pipeline group it falls under
  cat: z.string().default(''),            // membership | gift_card | event_ticket | coupon
  merchant: z.string().default(''),
  country: z.string().default(''),        // ISO-2 (→ flag emoji client-side)
  mime: z.string().default(''),           // image/* | application/pdf
  created_at: z.string().default(''),
  preview_url: z.string().default(''),    // signed (HMAC sig) thumb/full — no token in URL
  /** Сколько участников/исходников в группе (merchant × locale) актуального пула. */
  count: z.number().int().nonnegative().default(1),
});
export type WorkDesignerFeedItem = z.infer<typeof WorkDesignerFeedItemSchema>;

// GET /api/work/designer/examples?kind=source|final — «как у QA»: последние
// АПРУВНУТЫЕ примеры. source = принятые исходники-скрины (мембершипы),
// final = принятые готовые фреймы дизайнеров. Только просмотр.
export const WorkDesignerExamplesResSchema = z.object({
  ok: z.boolean(),
  kind: z.enum(['source', 'final']),
  items: z.array(z.object({
    id: z.string(),
    merchant: z.string().default(''),
    locale: z.string().default(''),
    country: z.string().default(''),
    checked_at: z.string().default(''),
    preview_url: z.string().default(''),
  })),
});
export type WorkDesignerExamplesRes = z.infer<typeof WorkDesignerExamplesResSchema>;

// GET /api/work/designer/feed
export const WorkDesignerFeedResSchema = z.object({
  shot: z.array(WorkDesignerFeedItemSchema).default([]),
  pdf: z.array(WorkDesignerFeedItemSchema).default([]),
});
export type WorkDesignerFeedRes = z.infer<typeof WorkDesignerFeedResSchema>;

// ─── stats (counts only — money intentionally absent) ─────────────────────────
// GET /api/work/designer/me
export const WorkDesignerMeResSchema = z.object({
  made: z.number().int().nonnegative().default(0),         // lifetime produced/uploaded
  sent_today: z.number().int().nonnegative().default(0),   // funnel: submitted today
  on_qa: z.number().int().nonnegative().default(0),        // funnel: pending eval
  accepted: z.number().int().nonnegative().default(0),     // funnel: passed
  work_alerts: z.boolean().default(true),                  // current toggle state
});
export type WorkDesignerMeRes = z.infer<typeof WorkDesignerMeResSchema>;

// ─── own packs (История — no money) ───────────────────────────────────────────
export const WorkDesignerPackSchema = z.object({
  id: z.string(),
  kind: z.enum(['shot', 'pdf']),
  cat: z.string().default(''),
  merchant: z.string().default(''),
  status: z.enum(['processing', 'review', 'accepted', 'rework']),
  created_at: z.string().default(''),
});
export type WorkDesignerPack = z.infer<typeof WorkDesignerPackSchema>;

// GET /api/work/designer/packs
export const WorkDesignerPacksResSchema = z.object({
  packs: z.array(WorkDesignerPackSchema).default([]),       // newest first; client groups by day
});
export type WorkDesignerPacksRes = z.infer<typeof WorkDesignerPacksResSchema>;

// ─── work-alerts toggle (logged) ──────────────────────────────────────────────
// POST /api/work/designer/notifPref
export const WorkDesignerNotifPrefReqSchema = z.object({ work_alerts: z.boolean() });
export type WorkDesignerNotifPrefReq = z.infer<typeof WorkDesignerNotifPrefReqSchema>;
export const WorkDesignerNotifPrefResSchema = z.object({ ok: z.boolean(), work_alerts: z.boolean() });
export type WorkDesignerNotifPrefRes = z.infer<typeof WorkDesignerNotifPrefResSchema>;

// ─── shared-pool claim / release / flag (0013) ────────────────────────────────
export const WorkSourceRefSchema = z.object({
  source_type: z.enum(['shot', 'pdf']),
  source_id: z.string().min(1),
});
export type WorkSourceRef = z.infer<typeof WorkSourceRefSchema>;

// POST /api/work/designer/claim — 30-minute lease; 409-style { ok:false } if held by another.
export const WorkDesignerClaimResSchema = z.object({
  ok: z.boolean(),
  expires_at: z.string().default(''),
  reason: z.string().default(''),          // 'claimed_by_other' when ok=false
});
export type WorkDesignerClaimRes = z.infer<typeof WorkDesignerClaimResSchema>;

// POST /api/work/designer/release
export const WorkDesignerReleaseResSchema = z.object({ ok: z.boolean() });
export type WorkDesignerReleaseRes = z.infer<typeof WorkDesignerReleaseResSchema>;

// POST /api/work/designer/flag — { source_type, source_id, comment }
export const WorkDesignerFlagReqSchema = WorkSourceRefSchema.extend({
  comment: z.string().trim().max(2000).default(''),
});
export type WorkDesignerFlagReq = z.infer<typeof WorkDesignerFlagReqSchema>;
export const WorkDesignerFlagResSchema = z.object({ ok: z.boolean() });
export type WorkDesignerFlagRes = z.infer<typeof WorkDesignerFlagResSchema>;

// GET /api/work/manager/flaggedSources — manager queue of open source flags.
export const WorkFlaggedSourceSchema = z.object({
  id: z.string(),
  source_type: z.enum(['shot', 'pdf']),
  source_id: z.string(),
  merchant: z.string().default(''),
  cat: z.string().default(''),
  comment: z.string().default(''),
  flagged_by: z.string().default(''),
  created_at: z.string().default(''),
});
export type WorkFlaggedSource = z.infer<typeof WorkFlaggedSourceSchema>;
export const WorkFlaggedSourcesResSchema = z.object({
  flags: z.array(WorkFlaggedSourceSchema).default([]),
});
export type WorkFlaggedSourcesRes = z.infer<typeof WorkFlaggedSourcesResSchema>;

// ─── claimed-source detail (what the plugin needs to produce variants) ────────
// GET /api/work/designer/source?type=&id=
export const WorkDesignerSourceResSchema = z.object({
  source_type: z.enum(['shot', 'pdf']),
  source_id: z.string(),
  status: z.string().default(''),
  master_url: z.string().default(''),     // signed URL to the PDF / screenshot
  merchant: z.string().default(''),
  cat: z.string().default(''),
  country: z.string().default(''),
  locale: z.string().default(''),
  // ticket PDFs carry the decoder-exact codes + best-effort semantic fields.
  codes: z.array(z.object({ type: z.string().default(''), value: z.string().default('') })).default([]),
  fields: z.record(z.string(), z.string()).default({}),  // event_name/date/venue/… + supplemental
});
export type WorkDesignerSourceRes = z.infer<typeof WorkDesignerSourceResSchema>;

// ─── Web Push ─────────────────────────────────────────────────────────────────
// GET /api/work/push/config — VAPID public key (public; safe to expose).
export const WorkPushConfigResSchema = z.object({ vapidPublicKey: z.string().default('') });
export type WorkPushConfigRes = z.infer<typeof WorkPushConfigResSchema>;

// POST /api/work/push/subscribe
export const WorkPushSubscribeReqSchema = z.object({
  endpoint: z.string().url(),
  p256dh: z.string().default(''),
  auth: z.string().default(''),
});
export type WorkPushSubscribeReq = z.infer<typeof WorkPushSubscribeReqSchema>;

// POST /api/work/push/unsubscribe
export const WorkPushUnsubscribeReqSchema = z.object({ endpoint: z.string() });
export type WorkPushUnsubscribeReq = z.infer<typeof WorkPushUnsubscribeReqSchema>;

export const WorkPushResSchema = z.object({ ok: z.boolean() });
export type WorkPushRes = z.infer<typeof WorkPushResSchema>;
